import {
  getCleanCpfCaepf,
  isCaepfDigits,
} from '@/features/quotes/utils/cpf-caepf-document.util';
import { validateCnpj } from '@/shared/utils';

export type CapitalGlobalDocumentIdType = '1' | '2' | '3';

type StoredCapitalGlobalCustomerIdentity = {
  documentType?: string;
  documentNumber: string;
  isOrgParty?: string;
};

/**
 * Documento normalizado (`[A-Z0-9]`, maiúsculo). CNPJ pode ser alfanumérico
 * (RFB) — nunca `/\D/g` aqui, senão o CNPJ encolhe e vira "CPF".
 */
function normalizeCapitalGlobalDocument(value: string): string {
  return getCleanCpfCaepf(value);
}

type ResolveCapitalGlobalIdTypeParams = {
  documentNumber: string;
  useIndividualDocument: boolean;
};

/** CAEPF: toggle individual + 14 dígitos NUMÉRICOS (InsureMO `TipoDocumento` = 3). */
export function isCaepfDocument(documentNumber: string): boolean {
  return isCaepfDigits(getCleanCpfCaepf(documentNumber));
}

/**
 * Resolve o `idType`/`TipoDocumento` do Capital Global:
 * - `'2'` CNPJ
 * - `'1'` CPF (11 dígitos)
 * - `'3'` CAEPF (14 dígitos no modo individual)
 */
export function resolveCapitalGlobalIdType({
  documentNumber,
  useIndividualDocument,
}: ResolveCapitalGlobalIdTypeParams): CapitalGlobalDocumentIdType {
  if (!useIndividualDocument) {
    return '2';
  }

  if (isCaepfDocument(documentNumber)) {
    return '3';
  }

  return '1';
}

export function isCapitalGlobalLegalPersonIdType(
  idType: CapitalGlobalDocumentIdType | string | undefined
): boolean {
  return String(idType ?? '') === '2';
}

/**
 * Resolve `TipoDocumento` a partir de dados já persistidos (proposta/cotação
 * carregada). Normaliza legado CAEPF (`documentType` `'1'` + 14 dígitos).
 */
export function resolveCapitalGlobalIdTypeFromStoredCustomer(
  params: StoredCapitalGlobalCustomerIdentity
): CapitalGlobalDocumentIdType | undefined {
  const docType = String(params.documentType ?? '').trim();
  const cleanDocument = normalizeCapitalGlobalDocument(params.documentNumber);

  if (docType === '3') {
    return '3';
  }

  if (docType === '2') {
    return '2';
  }

  if (docType === '1') {
    // Legado CAEPF gravado como '1'. Se vier CNPJ alfanumérico com docType '1'
    // (dado inconsistente), classifica como CNPJ pelo DV.
    if (isCaepfDocument(params.documentNumber)) return '3';
    if (cleanDocument.length === 14 && validateCnpj(cleanDocument)) return '2';
    return '1';
  }

  if (params.isOrgParty === 'Y') {
    return '2';
  }

  if (cleanDocument.length === 11) {
    return '1';
  }

  if (cleanDocument.length === 14) {
    // CNPJ (numérico ou alfanumérico) pelo DV; senão CAEPF numérico; senão indefinido.
    if (validateCnpj(cleanDocument)) return '2';
    return isCaepfDigits(cleanDocument) ? '3' : undefined;
  }

  return undefined;
}

export function isCapitalGlobalLegalPersonFromStoredCustomer(
  params: StoredCapitalGlobalCustomerIdentity
): boolean {
  const idType = resolveCapitalGlobalIdTypeFromStoredCustomer(params);
  if (!idType) {
    return false;
  }

  return isCapitalGlobalLegalPersonIdType(idType);
}

export function resolveCapitalGlobalIndividualDocumentFromApi(
  documentType: string | undefined,
  documentNumber: string,
  isOrgParty?: string
): boolean {
  const idType = resolveCapitalGlobalIdTypeFromStoredCustomer({
    documentType,
    documentNumber,
    isOrgParty,
  });

  if (idType) {
    return !isCapitalGlobalLegalPersonIdType(idType);
  }

  return normalizeCapitalGlobalDocument(documentNumber).length <= 11;
}

export function resolveCapitalGlobalDocumentLabel(
  documentType: string | undefined
): string {
  const docType = String(documentType ?? '').trim();

  if (docType === '2') {
    return 'CNPJ';
  }

  if (docType === '3') {
    return 'CAEPF';
  }

  return 'CPF';
}
